import { Search, LineChart, Lightbulb, Clapperboard } from 'lucide-react'

const features = [
  {
    icon: Search,
    title: 'Competitor Research',
    description:
      'Study competitor ads, isolate winning hooks, and identify the formats and angles worth testing next.',
    accent: 'from-cyan-400/80 to-sky-500/80',
    borderClass: 'border-cyan-400/15 hover:border-cyan-300/35',
  },
  {
    icon: LineChart,
    title: 'Market Research',
    description:
      'Surface audience pain points, category signals, and emerging themes before they show up in your ad account.',
    accent: 'from-fuchsia-400/75 to-violet-500/70',
    borderClass: 'border-fuchsia-400/15 hover:border-fuchsia-300/35',
  },
  {
    icon: Lightbulb,
    title: 'Creative Ideation',
    description:
      'Turn research into sharper concepts, messaging directions, and ad narratives your team can execute immediately.',
    accent: 'from-amber-300/80 to-orange-500/75',
    borderClass: 'border-amber-300/15 hover:border-amber-200/35',
  },
  {
    icon: Clapperboard,
    title: 'Ad Generation',
    description:
      'Generate image and video-ready creatives faster with AI workflows built for performance marketing teams.',
    accent: 'from-emerald-300/80 to-teal-500/75',
    borderClass: 'border-emerald-300/15 hover:border-emerald-200/35',
  },
]

export function FeaturesSection() {
  return (
    <section id="features" className="scroll-mt-28 px-4 py-20 sm:px-6 lg:px-8 lg:py-24">
      <div className="mx-auto max-w-7xl">
        <div className="text-center mb-16">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-cyan-300/85">
            Core capabilities
          </p>
          <h2 className="mt-4 text-3xl font-bold text-white sm:text-4xl">
            From research to creatives, everything in one place
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">
            Four connected workflows that help performance marketers move from signal to creative output without tool sprawl.
          </p>
        </div>

        <div className="landing-panel rounded-[2rem] p-6 sm:p-8 lg:p-10">
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
            {features.map((feature, index) => (
              <div
                key={feature.title}
                className={`feature-card group relative overflow-hidden rounded-[1.75rem] border bg-white/[0.03] p-8 opacity-0 animate-fade-in-up transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.05] lg:p-9 ${feature.borderClass}`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`pointer-events-none absolute inset-x-6 top-0 h-px bg-gradient-to-r ${feature.accent} opacity-80`} />
                <div className="pointer-events-none absolute -right-10 top-10 h-24 w-24 rounded-full bg-white/10 opacity-0 blur-3xl transition-opacity duration-300 group-hover:opacity-100" />
                <div className={`mb-6 inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-gradient-to-br ${feature.accent} shadow-[0_0_30px_rgba(56,189,248,0.18)]`}>
                  <feature.icon className="h-5 w-5 text-slate-950" />
                </div>
                <h3 className="mb-3 text-xl font-semibold text-white">{feature.title}</h3>
                <p className="text-sm leading-7 text-slate-300">{feature.description}</p>
                <div className="mt-7 h-1 overflow-hidden rounded-full bg-white/5">
                  <div className={`h-full w-1/3 rounded-full bg-gradient-to-r ${feature.accent} transition-all duration-500 group-hover:w-full`} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
